// API Service for PWA
import { googleAuthService } from './auth/index.js';
import { configService } from './config.js';

export class ApiService {
  constructor() {
    // Listen for backend changes
    window.addEventListener('backendChanged', (event) => {
      console.log('Backend changed, API service adapting:', event.detail);
    });
  }

  getApiBaseUrl() {
    return configService.getApiBaseUrl();
  }

  async checkHealth() {
    try {
      const authHeaders = await this.getAuthHeaders();
      console.log('Health check auth headers:', authHeaders);
      const baseUrl = this.getApiBaseUrl().replace('/api', '');
      const response = await fetch(`${baseUrl}/api/health`, {
        headers: authHeaders,
        mode: configService.isLocal() ? 'cors' : 'cors'
      });
      if (!response.ok) {
        console.error('Health check failed:', response.status, response.statusText);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const result = await response.json();
      console.log('Health check result:', result);
      return result;
    } catch (error) {
      console.error('Health check failed:', error);
      throw error;
    }
  }

  async request(endpoint, options = {}) {
    const url = `${this.getApiBaseUrl()}${endpoint}`;

    // Get authentication headers if user is signed in
    const authHeaders = await this.getAuthHeaders();

    const { headers: optionHeaders, ...restOptions } = options;
    const fetchOptions = {
      ...restOptions,
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders,
        ...optionHeaders,
      },
    };

    // Add CORS mode for local backend
    if (configService.isLocal()) {
      fetchOptions.mode = 'cors';
    }

    const response = await fetch(url, fetchOptions);

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  async get(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'GET' });
  }

  async post(endpoint, data, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async put(endpoint, data, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async delete(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'DELETE' });
  }

  async getAuthHeaders() {
    const headers = {};

    // Add Google user authentication headers (for user identification)
    if (googleAuthService.isSignedIn()) {
      const userInfo = googleAuthService.getUserInfo();
      if (userInfo) {
        headers['X-User-ID'] = userInfo.id;
        headers['X-User-Email'] = userInfo.email;
        // Keep Google token as secondary auth if needed
        const googleToken = googleAuthService.getAccessToken();
        if (googleToken) {
          headers['X-Google-Token'] = googleToken;
        }
      }
    }
    
    return headers;
  }

  async createSession(name, organizerName, languages, enable_tts = true) {
    return this.request('/sessions', {
      method: 'POST',
      body: JSON.stringify({
        name,
        organizer_name: organizerName,
        languages,
        enable_tts,
      }),
    });
  }

  async getSession(sessionId) {
    return this.request(`/sessions/${sessionId}`);
  }

  async addLanguageToSession(sessionId, languageCode) {
    return this.request(`/sessions/${sessionId}/languages/${languageCode}`, {
      method: 'POST'
    });
  }

  async getAllSessions() {
    try {
      const sessions = await this.request('/sessions');
      console.log('Retrieved sessions:', sessions);
      
      // Ensure we're returning an array and handle different response formats
      if (Array.isArray(sessions)) {
        return sessions;
      } else if (sessions && sessions.sessions && Array.isArray(sessions.sessions)) {
        return sessions.sessions;
      } else if (sessions && typeof sessions === 'object') {
        // If it's an object with session data, convert to array
        return Object.values(sessions);
      } else {
        console.warn('Unexpected sessions response format:', sessions);
        return [];
      }
    } catch (error) {
      console.error('Failed to get all sessions:', error);
      throw error;
    }
  }

  async updateSession(sessionId, updates) {
    return this.request(`/sessions/${sessionId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  async deleteSession(sessionId) {
    await this.request(`/sessions/${sessionId}`, {
      method: 'DELETE',
    });
  }

  async generateQRCode(sessionId) {
    const authHeaders = await this.getAuthHeaders();
    const url = `${this.getApiBaseUrl()}/sessions/${sessionId}/qr-code`;
    const fetchOptions = {
      headers: authHeaders
    };

    if (configService.isLocal()) {
      fetchOptions.mode = 'cors';
    }

    const response = await fetch(url, fetchOptions);
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  }

  async translateText(text, sourceLang, targetLang) {
    return this.request('/translate', {
      method: 'POST',
      body: JSON.stringify({
        text,
        source_language: sourceLang,
        target_language: targetLang,
      }),
    });
  }
}

export const apiService = new ApiService();
